import { todayStr } from './utils.js';
import { getAgeBracketDesc } from './tracker.js';

export const OPUS = 'claude-opus-4-20250514';

export function opusAllowedToday(childId) {
  try {
    const key = 'rq:opus-last:' + childId;
    const last = localStorage.getItem(key);
    const today = todayStr();
    if (last === today) return false;
    localStorage.setItem(key, today);
    return true;
  } catch { return true; }
}

let _aiFailed = false;
export function aiHasFailed() { return _aiFailed; }
const warnedUnavailable = new Set();

function warnAIUnavailable(scope) {
  const key = scope || 'default';
  if (warnedUnavailable.has(key)) return;
  warnedUnavailable.add(key);
  console.warn('AI endpoint unavailable, using offline content');
}

export async function callAI(prompt, model) {
  try {
    const r = await fetch("/api/ai", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt, model: model || undefined })
    });
    if (!r.ok) {
      _aiFailed = true;
      warnAIUnavailable('fetch');
      return null;
    }
    const d = await r.json();
    if (d?.error) {
      _aiFailed = true;
      warnAIUnavailable('response');
      return null;
    }
    _aiFailed = false;
    return d;
  } catch(e) {
    _aiFailed = true;
    warnAIUnavailable('network');
    return null;
  }
}

export async function analyzeSession(profile, online) {
  if (!online) return null;
  if (!opusAllowedToday(profile.id)) return null;
  const t = profile.tracker;
  if (!t.log || t.log.length < 5) return null;
  const ageBracket = getAgeBracketDesc(profile);
  const lv = t.currentLevel;
  try {
    const result = await callAI(`You are a reading specialist analyzing a child's learning session. The child is a ${ageBracket}, currently at reading Level ${lv}/15. They have completed ${t.roundsAtLevel||0} rounds at this level and ${t.rounds} total rounds.

Here are their last ${t.log.length} interactions (m=mode, w=word, c=correct, ms=response time in milliseconds, lv=level at time):
${JSON.stringify(t.log.slice(-30))}

Weak words they struggle with: ${JSON.stringify(t.weakWords.slice(0,10))}

Mode stats — phonics: ${t.ms.phonics.c}/${t.ms.phonics.t} correct, sight: ${t.ms.sight.c}/${t.ms.sight.t}, stories: ${t.ms.stories.c}/${t.ms.stories.t}, bubbles: ${t.ms.bubbles.c}/${t.ms.bubbles.t}

Key analysis points:
- Response times under 2 seconds on stories likely means guessing without reading
- Response times over 10 seconds may indicate struggle or distraction
- Look for specific phoneme patterns in wrong answers (th vs sh, blends vs digraphs)
- Consider if the child leveled up too fast (high errors right after level change)
- "slow" pacing means they need 18 correct streak + 4 rounds to level up. Use for kids who are fragile or inconsistent.
- "normal" means 12 correct streak + 3 rounds. Default.
- "fast" means 8 correct streak + 2 rounds. Only for kids clearly crushing it with high accuracy AND good response times.

Return ONLY this JSON (no markdown, no explanation):
{"levelRec":${lv},"strengths":["skill1"],"weaknesses":["skill1"],"focus":"what to emphasize next","phonicsAdj":"content instruction for phonics prompts","storiesAdj":"content instruction for story prompts","sightAdj":"content instruction for sight word prompts","pacing":"slow|normal|fast","parentNote":"2-3 sentence insight for the parent. Be specific about what you observed — mention response times, specific words, patterns. Give one actionable tip."}`, OPUS);
    if (result && result.parentNote) return result;
    return null;
  } catch {
    warnAIUnavailable('analysis');
    return null;
  }
}
